import { FormHelperText, Stack, Typography } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import UploadCSVButton from "../components/UploadCSVButton";
import { Company } from "../models/company.model";
import { CompanyStore } from "../models/company.store.model";
import { forwardRef, useImperativeHandle } from "react";
import { FormInputProps } from "../models/form.model";
import { useConfig } from "./ConfigContext";

export default forwardRef((props: {}, ref: any) => {
  const methods = useForm<{ stores: CompanyStore[] }>({});
  const { handleSubmit, control, setError } = methods;
  const company = useConfig().loggedUser?.company;

  useImperativeHandle(ref, () => ({
    onSubmit() {
      return new Promise((resolve, reject) => {
        handleSubmit((d) => {
          const errors: string[] = [];
          const stores = (d.stores || []).map((d) => ({
            ...d,
            company: company as Company,
          }));
          stores.forEach((s, i) => {
            let tmpError = [];
            Object.keys(s).forEach((k) => {
              if (["postCode", "trialDay", "phoneNumber"].includes(k)) return;
              if (!((s as any)[k] || "").toString().trim()) {
                tmpError.push(`does not have ${k} value`);
              }
            });
            if (!s.company.storeType.split(",").includes(s.storeType)) {
              tmpError.push("does not have correct store type");
            }
            if (!s.company.area.split(",").includes(s.area)) {
              tmpError.push("does not have correct area");
            }
            if (!s.company.region.split(",").includes(s.region)) {
              tmpError.push("does not have correct region");
            }
            if (tmpError.length > 0) {
              errors.push(`Record row ${i + 1} ${tmpError.join(", ")}`);
            }
          });
          const message = `Please fix below errors in your csv and try again: \n ${errors.join(
            "\n"
          )}`;
          if (errors.length > 0) {
            setError("stores", { message });
            reject(message);
            return;
          }
          resolve(stores);
        }, reject)();
      });
    },
  }));

  return (
    <Stack
      direction="column"
      className="padding-t-big padding-b-big"
      spacing={2}
    >
      {company && (
        <ControlImportUpload
          key={company.id}
          control={control}
          name="stores"
          setError={setError}
          selectedCompany={company}
        />
      )}
    </Stack>
  );
});

function ControlImportUpload(
  props: FormInputProps & { selectedCompany: Company }
) {
  const { name, control, rules, setError, selectedCompany } = props;
  const setErrorMsg = (message: string) => {
    setError(name, { type: "manual", message });
  };
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({
        field: { onChange, value = [] },
        fieldState: { error },
        formState,
      }) => (
        <>
          <UploadCSVButton<CompanyStore>
            keys={[
              "name",
              "storeId",
              "area",
              "region",
              "address",
              "storeType",
              "postCode",
              "trialDay",
              "phoneNumber",
            ]}
            key={value.id}
            onChange={(data) => {
              onChange(data);
            }}
            sample={`name,storeId,area,region,address,storeType,postCode,trialDay,phoneNumber\nsample store,xxx-id,select from ${selectedCompany.area.replace(
              /,/g,
              " "
            )},select from ${selectedCompany.region.replace(
              /,/g,
              " "
            )},some where, select from ${(
              selectedCompany.storeType || ""
            ).replace(/,/g, " ")}, xxxx, date YYYY-MM-DD, xxxx`}
          />
          <Typography sx={{ mt: 2 }} variant="subtitle1">
            Uploaded {value.length} records to import
          </Typography>
          <FormHelperText sx={{ whiteSpace: "pre-line" }} error={!!error}>
            {error ? error.message : ""}
          </FormHelperText>
        </>
      )}
    />
  );
}
